import config from '../config';

enum CWTimeType {
    ABSOLUTE = 'ABSOLUTE',
    RELATIVE = 'RELATIVE',
  }
  
  enum CWTimeZone {
    LOCAL = 'Local',
    UTC = 'UTC',
  }
  
  interface MakeCloudwatchInsightsUrlInput {
    region?: string;
    start: string | number;
    end?: string | number;
    timeType?: CWTimeType;
    sources?: string[];
    query?: string[] | string;
  }
  
  const S1 = '$257E';
  const S2 = '$2528';
  const S3 = '$2527';
  const S4 = '$2529';
  // const S1 = '!1>>>>';
  // const S2 = '!2++++';
  // const S3 = '!3::::';
  // const S4 = '!4****';
  
  function translateStrToUrlFormat(s: string): string {
    return s
      .split('')
      .map((ch) =>
        ch.match(/^[a-zA-Z0-9-.]$/)
          ? ch
          : ['*', ch.charCodeAt(0).toString(16).padStart(2, '0')].join(''),
      )
      .join('');
  }
  
  function cwFmtObject(
    obj: Record<string, string | number | boolean | string[]>,
    prefix: string[] = [S1, S2],
  ): string {
    const s: string[] = [];
    Object.entries(obj).forEach(([k, v], i) => {
      if (i === 0) {
        s.push(...prefix, k);
      } else {
        s.push(S1, k);
      }
  
      if (typeof v === 'boolean' || typeof v === 'number') {
        s.push(S1, String(v));
      } else if (Array.isArray(v)) {
        s.push(cwFmtArray(v));
      } else {
        s.push(S1, S3, translateStrToUrlFormat(String(v)));
      }
    });
    return s.join('');
  }
  
  function cwFmtArray(arr: string[]): string {
    return [S1, S2, ...arr.flatMap((x) => [S1, S3, translateStrToUrlFormat(x)])].join('');
  }
  
  function mkHashPart(input: MakeCloudwatchInsightsUrlInput): string {
    const timeType =
      input.timeType !== undefined
        ? input.timeType
        : typeof input.start === 'number'
        ? CWTimeType.RELATIVE
        : CWTimeType.ABSOLUTE;
    const editorString =
      input.query === undefined
        ? ''
        : typeof input.query === 'string'
        ? input.query
        : input.query.join('\n| ');
    const configurationObjectFormatted = cwFmtObject({
      editorString,
      start: input.start,
      end: input.end === undefined ? 0 : input.end,
      timeType,
      ...(timeType !== CWTimeType.ABSOLUTE ? {} : { tz: CWTimeZone.LOCAL }),
      ...(timeType !== CWTimeType.RELATIVE ? {} : { unit: 'seconds' }),
      isLiveTail: false,
    });
    const sourcesObjectFormatted = cwFmtObject(
      {
        source: input.sources === undefined ? [] : input.sources,
      },
      [S1],
    );
    return [
      'logsV2:logs-insights$3FqueryDetail$3D',
      configurationObjectFormatted,
      sourcesObjectFormatted,
      S4,
      S4,
    ].join('');
  }
  
  /**
   * Based on https://stackoverflow.com/questions/68429312/generate-aws-logs-insights-url-with-query-and-search-creteria
   */
export function makeCloudWatchInsightsUrl(input: MakeCloudwatchInsightsUrlInput): string {
    const region = input.region === undefined ? process.env.AWS_REGION : input.region;
    const urlBase = `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}`;
    const urlHash = mkHashPart(input);
    return [urlBase, urlHash].join('#');
}

export const generateCWLink = (from: string | number, to: string | number, query: string | string) => {
    return makeCloudWatchInsightsUrl({
        region: config.AWSRegion,
        start: from,
        end: to,
        timeType: CWTimeType.ABSOLUTE,
        sources: config.cloudWatchLogSources,
        query: query,
    });
}
